// STAGING-LAUNCH-READINESS-002-R2 — custom exit sheet for the header «×» (GlobalAppClose).
import { useEffect } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { ExitRetentionState } from "./exit-retention-v2.js";
import { emitEvent } from "./events-v2.js";

export interface RecentProductChip {
  id: string;
  title: string;
}

export function ExitRetentionSheetV2({
  open,
  lang,
  state,
  cartCount,
  referralAvailable,
  recentProducts,
  onContinueShopping,
  onOpenCart,
  onOpenInvite,
  onOpenProduct,
  onConfirmClose,
  onDismiss,
}: {
  open: boolean;
  lang: StoreV2Lang;
  state: ExitRetentionState;
  cartCount: number;
  referralAvailable: boolean;
  recentProducts: RecentProductChip[];
  onContinueShopping: () => void;
  onOpenCart: () => void;
  onOpenInvite: () => void;
  onOpenProduct: (id: string) => void;
  onConfirmClose: () => void;
  onDismiss: () => void;
}) {
  const en = lang === "en";
  // Keep props for call-site compatibility; product rail removed in R8.
  void recentProducts;
  void onOpenProduct;

  useEffect(() => {
    if (open) emitEvent("exit_sheet_shown", { state, cart_count: cartCount });
  }, [open, state, cartCount]);

  if (!open) return null;

  const title =
    state === "post_order"
      ? en
        ? "Order placed! Before you go…"
        : "Замовлення оформлено! Перш ніж вийти…"
      : state === "checkout"
        ? en
          ? "Checkout isn't finished yet"
          : "Оформлення ще не завершено"
        : state === "cart"
          ? en
            ? "You have items in your cart"
            : "У вашому кошику є товари"
          : en
            ? "Leaving already?"
            : "Вже йдете?";

  const hint =
    state === "post_order"
      ? referralAvailable
        ? en
          ? "Invite a friend and you'll both get a bonus on your next order."
          : "Запросіть друга — ви обидва отримаєте бонус на наступне замовлення."
        : en
          ? "You can track your order any time in “My orders”."
          : "Ви можете стежити за замовленням у розділі «Мої замовлення»."
      : state === "checkout"
        ? en
          ? "Your details will be kept — you can finish checkout later."
          : "Ваші дані збережуться — можна завершити оформлення пізніше."
        : state === "cart"
          ? en
            ? `${cartCount} item(s) will stay saved in your cart.`
            : `${cartCount} товар(и) залишаться збереженими у кошику.`
          : en
            ? "Take a last look before you close the store."
            : "Погляньте востаннє, перш ніж закрити магазин.";

  return (
    <div
      className="sv2-exit-sheet-overlay"
      data-testid="sv2-exit-sheet-overlay"
      onClick={onDismiss}
      role="presentation"
    >
      <div
        className="sv2-exit-sheet"
        data-testid="sv2-exit-sheet"
        data-exit-state={state}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sv2-exit-sheet-title">{title}</div>
        <div className="sv2-exit-sheet-hint">{hint}</div>

        {/* COMMERCE-LAUNCH-167A-R8 — remove non-functional product rail from exit overlay. */}

        <div className="sv2-exit-sheet-actions">
          {state === "cart" || state === "checkout" ? (
            <>
              <button
                type="button"
                className="sv2-cta"
                data-testid="sv2-exit-continue-shopping"
                onClick={() => {
                  emitEvent("continue_shopping", { state });
                  onContinueShopping();
                }}
              >
                {en ? "Continue shopping" : "Продовжити покупки"}
              </button>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-exit-open-cart"
                onClick={() => {
                  emitEvent("open_cart", { state });
                  onOpenCart();
                }}
              >
                {en ? "Go to cart" : "Перейти в кошик"}
              </button>
            </>
          ) : state === "post_order" && referralAvailable ? (
            <button
              type="button"
              className="sv2-cta"
              data-testid="sv2-exit-invite-friends"
              onClick={() => {
                emitEvent("referral_opened", { state });
                onOpenInvite();
              }}
            >
              🎁 {en ? "Invite a friend" : "Запросити друга"}
            </button>
          ) : (
            <button
              type="button"
              className="sv2-cta"
              data-testid="sv2-exit-continue-shopping"
              onClick={() => {
                emitEvent("continue_shopping", { state });
                onContinueShopping();
              }}
            >
              {en ? "Keep browsing" : "Продовжити перегляд"}
            </button>
          )}
          <button
            type="button"
            className="sv2-cta sv2-tertiary"
            data-testid="sv2-exit-confirm-close"
            onClick={() => {
              emitEvent("close_confirmed", { state });
              onConfirmClose();
            }}
          >
            {en ? "Close the store" : "Закрити магазин"}
          </button>
        </div>
      </div>
    </div>
  );
}
